import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	ExpectedLocationUseOfCard,
	ExpectedUseOfCard,
	HowToPayForInvoices,
	UseCardForTransactionsLocation,
} from "@/types/form";

export interface CreditCardCustomerForm {
	isPEP: boolean;
	citizenship: string[];
	expectedUseOfCard: ExpectedUseOfCard[];
	expectedLocationUseOfCard: ExpectedLocationUseOfCard[];
	howToPayForInvoices: HowToPayForInvoices[];
	useCardForTransactions: boolean;
	useCardForTransactionsLocation?: UseCardForTransactionsLocation[];
	withdrawMoreThan50k: boolean;
}

export type KYCKey = keyof Omit<
	CreditCardCustomerForm,
	"isPEP" | "citizenship"
>;

export const useCreditCardCustomerForm = () => {
	const t = useTranslations("forms.errors");

	// Avoid annotating with `z.ZodSchema<CreditCardCustomerForm>`: doing so widens
	// the type to `ZodType<...>`, which @hookform/resolvers v5's `zodResolver`
	// rejects (it expects the narrower `Zod3Type<...>` returned by z.object()).
	const ApplicationSchema = z
		.object({
			isPEP: z.boolean({
				error: t("general.required"),
			}),
			citizenship: z.array(z.string()).min(1, t("multiselect.min1Option")),
			expectedUseOfCard: z
				.array(z.nativeEnum(ExpectedUseOfCard))
				.min(1, t("multiselect.min1Option")),
			expectedLocationUseOfCard: z
				.array(z.nativeEnum(ExpectedLocationUseOfCard))
				.min(1, t("multiselect.min1Option")),
			howToPayForInvoices: z
				.array(z.nativeEnum(HowToPayForInvoices))
				.min(1, t("multiselect.min1Option")),
			useCardForTransactions: z.boolean({
				error: t("general.required"),
			}),
			useCardForTransactionsLocation: z
				.array(z.nativeEnum(UseCardForTransactionsLocation))
				.optional(),
			withdrawMoreThan50k: z.boolean({
				error: t("general.required"),
			}),
		})
		.superRefine((data, ctx) => {
			if (
				data.useCardForTransactions &&
				!data.useCardForTransactionsLocation?.length
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: t("multiselect.min1Option"),
					path: ["useCardForTransactionsLocation"],
				});
			}
		});

	return {
		// The third generic pins TTransformedValues to the same shape as
		// TFieldValues. Required after bumping @hookform/resolvers to v5,
		// which otherwise infers the schema's post-transform output type and
		// breaks compatibility with @leabankab/component-library (built against
		// resolvers v3 where the third generic was not threaded through).
		useForm: useForm<CreditCardCustomerForm, unknown, CreditCardCustomerForm>({
			resolver: zodResolver(ApplicationSchema),
			mode: "onTouched",
			defaultValues: {
				isPEP: undefined,
				citizenship: [],
				expectedUseOfCard: [],
				expectedLocationUseOfCard: [],
				howToPayForInvoices: [],
				useCardForTransactions: undefined,
				useCardForTransactionsLocation: [],
				withdrawMoreThan50k: undefined,
			},
		}),
		schema: ApplicationSchema,
	};
};
